<template>
  <div class="wg-card wg-gwiz" :id="embedded ? 'wg-gsc' : null">
    <div class="wg-gwiz-title">
      {{ changing ? 'Change Google Search Console account' : 'Connect Google Search Console' }}
    </div>
    <p class="wg-gwiz-sub">
      {{ changing
        ? 'Switch to a different Google account or Search Console property. Your tracked URLs and history are kept.'
        : 'This lets us see which keywords your site already ranks for, so we can build content plans.' }}
    </p>

    <!-- GoogleLogin.vue card -->
    <div class="wg-glogin">
      <div class="wg-glogin-top">
        <div class="wg-glogin-brand">
          <div class="wg-glogin-gmark" aria-hidden="true">G</div>
          <div class="wg-glogin-user">
            <template v-if="googleAccount">
              <div class="nm">{{ googleAccount.googleName || googleAccount.googleEmail }}</div>
              <div class="em">{{ googleAccount.googleEmail }}</div>
            </template>
            <template v-else>
              <div class="nm ph">No account connected</div>
              <div class="em ph">Connect your Google account to continue</div>
            </template>
          </div>
        </div>
        <div class="wg-glogin-actions">
          <span v-if="googleAccount" class="wg-connected-badge">Connected</span>
          <button
            v-if="googleAccount"
            type="button"
            class="wg-gbtn ghost"
            @click="changeAccount"
          >Change account</button>
          <google-auth-code-button
            v-else
            label="Connect"
            @code="onAuthCode"
            @error="onAuthError"
          />
        </div>
      </div>
      <div class="wg-glogin-banner">
        <i class="material-icons-outlined bico" aria-hidden="true">lock</i>
        <span>We only access Google Search Console data. Nothing else.</span>
      </div>
    </div>

    <a
      class="wg-help-gsc"
      href="https://help.pageoptimizer.pro/article/connect-google-search-console-to-pop"
      target="_blank"
      rel="noopener noreferrer"
    >
      <i class="material-icons-outlined">help_outline</i> How to connect GSC
    </a>

    <div v-if="googleAccount" class="wg-gfields">
      <div class="wg-gfield">
        <div class="card-small-title">Select location<span class="req"> *</span></div>
        <div class="wg-select-wrap">
          <select class="wg-select" v-model="locationCode">
            <option v-for="loc in availableLocations" :key="loc.location_code" :value="loc.location_code">
              {{ loc.location_name }}
            </option>
          </select>
          <i class="material-icons chev">expand_more</i>
        </div>
        <div class="wg-country-row">
          <wg-toggle v-model="countryEnabled" small />
          <div class="lbl">Enable country</div>
        </div>
      </div>

      <div class="wg-gfield">
        <div class="card-small-title">Select language<span class="req"> *</span></div>
        <div class="wg-select-wrap">
          <select class="wg-select" v-model="languageCode">
            <option v-for="lang in targetLanguages" :key="lang.language_code" :value="lang.language_code">
              {{ lang.language_name }}
            </option>
          </select>
          <i class="material-icons chev">expand_more</i>
        </div>
      </div>

      <div class="wg-gfield">
        <div class="card-small-title">Select your domain from GSC<span class="req"> *</span></div>
        <div class="wg-dompick" v-click-outside="closePropertyMenu">
          <button
            type="button"
            class="wg-dompick-field"
            :class="{ open: propertyMenuOpen, 'is-disabled': loadingProperties }"
            @click="togglePropertyMenu"
          >
            <span v-if="loadingProperties" class="ph">
              <span class="wg-spin dark"></span> Loading properties…
            </span>
            <span v-else-if="propertyUrl" class="val">{{ propertyUrl }}</span>
            <span v-else class="ph">Choose a property</span>
            <i class="material-icons chev">expand_more</i>
          </button>

          <div v-if="propertyMenuOpen" class="wg-dompick-menu">
            <div class="dsearch">
              <i class="material-icons">search</i>
              <input ref="propertySearch" placeholder="Search properties" v-model="propertySearch" />
            </div>
            <div class="dlist">
              <div v-if="filteredProperties.length === 0" class="dempty">
                No properties match “{{ propertySearch }}”
              </div>
              <button
                v-for="p in filteredProperties"
                :key="p.url"
                type="button"
                class="ditem"
                :class="{ invalid: !p.matchesProject, sel: propertyUrl === p.url }"
                @click="pickProperty(p)"
              >
                <span class="du">
                  <span class="u">{{ p.url }}</span>
                  <span class="m">
                    {{ p.matchesProject ? (p.permissionLevel || '') : `Doesn't match ${projectDomain}` }}
                  </span>
                </span>
                <i v-if="propertyUrl === p.url" class="material-icons dc">check</i>
              </button>
            </div>
            <div class="dfoot">
              Only properties for <b>{{ projectDomain }}</b> can be selected for this project.
            </div>
          </div>
        </div>
        <div v-if="propertyUrl" class="wg-dompick-sel">
          <i class="material-icons">check</i>
          <span>Tracking <b>{{ propertyUrl }}</b></span>
          <button type="button" class="clr" @click="propertyUrl = ''">Change</button>
        </div>
      </div>

      <div class="wg-gfield">
        <div class="card-small-title">Date period</div>
        <div class="wg-select-wrap">
          <select class="wg-select" v-model="datePeriod">
            <option v-for="p in availablePeriods" :key="p.value" :value="p.value">{{ p.label }}</option>
          </select>
          <i class="material-icons chev">expand_more</i>
        </div>
      </div>
    </div>

    <!-- required once the account is connected -->
    <template v-if="googleAccount">
      <div class="wg-consent">
        <label class="wg-cbx" :class="{ checked: monitoringEnabled }" @click.prevent="monitoringEnabled = !monitoringEnabled">
          <span class="cbx-box"><i v-if="monitoringEnabled" class="material-icons">check</i></span>
          <span class="lbl">Enable ongoing performance monitoring</span>
        </label>
        <div class="ctext">
          I authorize POP to access my Google Search Console data on an ongoing basis to monitor
          keyword rankings, impressions, clicks, and CTR. POP will check performance
          {{ freqAdverb }} and generate Wins reports.<br />
          We only access Search Console data, nothing else from your Google account. You can revoke
          access at any time. Optional: without it, Wins reports pause until you enable it in Settings.
        </div>
      </div>
      <div class="wg-terms">
        <label class="wg-cbx" :class="{ checked: termsAccepted }" @click.prevent="termsAccepted = !termsAccepted">
          <span class="cbx-box"><i v-if="termsAccepted" class="material-icons">check</i></span>
          <span class="lbl">
            I agree to the
            <a href="#" @click.prevent.stop="termsDoc = 'terms'">Performance Monitoring Terms &amp; Conditions</a>
            and
            <a href="#" @click.prevent.stop="termsDoc = 'policy'">Data Access Policy.</a>
          </span>
        </label>
      </div>
      <wg-terms-modal v-if="termsDoc" :doc="termsDoc" @close="termsDoc = null" />
    </template>

    <div class="wg-gwiz-foot">
      <button v-if="!embedded" class="wg-pill outline" @click="$emit('back')">
        <i class="material-icons">arrow_back</i> Back
      </button>
      <div class="sp"></div>
      <div v-if="googleAccount && !ready" class="wg-gwiz-need">
        {{ !propertyUrl ? 'Select your GSC domain to continue' : 'Accept the terms to continue' }}
      </div>
      <button
        v-if="googleAccount || isAdmin"
        class="wg-pill solid"
        :disabled="!ready || submitting"
        @click="complete"
      >
        <span v-if="submitting" class="wg-spin"></span>
        {{ changing ? 'Save changes' : 'Complete setup' }}
      </button>
    </div>
  </div>
</template>

<script>
import GoogleAuthCodeButton from '@/components/SitesSingleView/WhiteGlove/Settings/GoogleAuthCodeButton.vue'
import WgTermsModal from '@/components/SitesSingleView/WhiteGlove/Settings/WgTermsModal.vue'
import WgToggle from '@/components/SitesSingleView/WhiteGlove/Settings/WgToggle.vue'
import languages from '@/consts/dataforseo_languages.json'
import dataForSeoLocations from '@/consts/dataforseo_locations.json'
import { DATE_RANGE_OPTIONS } from '@/components/Campaigns/keyword-wizard/DateRangeSelect.vue'

export default {
  name: 'GscConnectWizard',
  components: { GoogleAuthCodeButton, WgTermsModal, WgToggle },
  inject: ['wgToast'],
  props: {
    site: {
      type: Object,
      default: () => ({}),
    },
    /** Existing connection (change account / reconnect flows), or null. */
    connection: {
      type: Object,
      default: null,
    },
    /** Mission-control admin mode: keeps the submit button visible while
     * no Google account is connected (after "Change account"). */
    isAdmin: {
      type: Boolean,
      default: false,
    },
    freqAdverb: {
      type: String,
      default: 'quarterly',
    },
    /** Rendered inline in the Settings card stack (no Back button, carries the wg-gsc anchor). */
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    const conn = this.connection
    return {
      googleAccount: conn && conn.googleEmail
        ? { id: null, googleEmail: conn.googleEmail, googleName: conn.googleName }
        : null,
      properties: [],
      loadingProperties: false,
      propertyMenuOpen: false,
      propertySearch: '',
      propertyUrl: (conn && conn.propertyUrl) || '',
      locationCode: conn && conn.locationCode ? this.coerceLocation(conn.locationCode) : 2840,
      languageCode: (conn && conn.languageCode) || 'en',
      countryEnabled: conn ? Boolean(conn.countryEnabled) : true,
      datePeriod: (conn && conn.initialDatePeriod) || 'last_28_days',
      monitoringEnabled: conn ? Boolean(conn.monitoringEnabled) : false,
      termsAccepted: Boolean(conn),
      termsDoc: null, // 'terms' | 'policy' | null

      submitting: false,
      availableLocations: dataForSeoLocations.locations,
      targetLanguages: languages,
      availablePeriods: DATE_RANGE_OPTIONS,
    }
  },
  computed: {
    changing () {
      return Boolean(this.connection && this.connection.googleEmail)
    },
    projectDomain () {
      const url = this.site.baseURL || ''
      return url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '')
    },
    filteredProperties () {
      const query = this.propertySearch.trim().toLowerCase()
      if (!query) return this.properties
      return this.properties.filter((p) => p.url.toLowerCase().includes(query))
    },
    ready () {
      return Boolean(
        this.googleAccount &&
        this.propertyUrl &&
        this.locationCode &&
        this.languageCode &&
        this.termsAccepted
      )
    },
  },
  watch: {
    // On a hard refresh the sites list arrives after this mounts, so the
    // mounted-time loadProperties() has no site id yet. Retry once it's known
    // (same 'site.id' watcher pattern as the sibling Settings cards).
    'site.id' (id) {
      if (id && this.googleAccount && !this.properties.length && !this.loadingProperties) {
        this.loadProperties()
      }
    },
  },
  mounted () {
    // Reconnect / change-account flows can list properties through the
    // existing site connection right away.
    if (this.googleAccount) {
      this.loadProperties()
    } else {
      // An account connected elsewhere (campaigns / audit flow GoogleLogin)
      // is reusable here - no second popup needed.
      this.$store.dispatch('getGoogleAccessToken')
        .then((response) => {
          const data = response && response.data
          if (this.googleAccount || !data || data.status !== 'SUCCESS' || !data.googleEmail) return
          this.googleAccount = {
            id: null,
            googleEmail: data.googleEmail,
            googleName: data.googleName || data.googleEmail,
          }
          this.loadProperties()
        })
        .catch(() => {})
    }
  },
  methods: {
    coerceLocation (value) {
      const code = parseInt(value, 10)
      return Number.isFinite(code) ? code : value
    },
    changeAccount () {
      // Just clears the picked account locally so the Connect button shows;
      // the popup only opens when the user explicitly clicks Connect. The
      // server-side account stays untouched until a new one is connected.
      this.googleAccount = null
      this.properties = []
      this.propertyUrl = ''
      this.propertySearch = ''
      this.propertyMenuOpen = false
    },
    onAuthCode (code) {
      this.$store.dispatch('connectGoogleAccount', { authCode: code })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.googleAccount = response.data.googleAccount
            this.propertyUrl = ''
            this.wgToast('Google account connected')
            this.loadProperties()
          } else {
            const msg = (response.data && response.data.msg) || 'Could not connect the Google account'
            this.wgToast(msg, null, 'error')
          }
        })
        .catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.msg) ||
            'Could not connect the Google account'
          this.wgToast(msg, null, 'error')
        })
    },
    onAuthError (message) {
      this.wgToast(message || 'Google sign-in failed', null, 'error')
    },
    loadProperties () {
      if (!this.site || !this.site.id) return
      this.loadingProperties = true
      this.$store.dispatch('listGscProperties', this.site.id)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            // matching properties first, like DomainSelector
            const properties = response.data.properties || []
            this.properties = [
              ...properties.filter((p) => p.matchesProject),
              ...properties.filter((p) => !p.matchesProject),
            ]
            // Like the legacy DomainSelector: pre-select the property that
            // matches the project domain (sc-domain first) if none picked yet.
            if (!this.propertyUrl) {
              const matching = this.properties.filter((prop) => prop.matchesProject)
              const preferred = matching.find((prop) => prop.url.startsWith('sc-domain:')) || matching[0]
              if (preferred) this.propertyUrl = preferred.url
            }
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loadingProperties = false
        })
    },
    togglePropertyMenu () {
      if (this.loadingProperties) return
      this.propertyMenuOpen = !this.propertyMenuOpen
      if (this.propertyMenuOpen) {
        this.$nextTick(() => {
          if (this.$refs.propertySearch) this.$refs.propertySearch.focus()
        })
      }
    },
    closePropertyMenu () {
      this.propertyMenuOpen = false
    },
    pickProperty (property) {
      if (!property.matchesProject) {
        this.wgToast(`${property.url.replace(/^sc-domain:/, '')} doesn't match this project`, null, 'error')
        return
      }
      this.propertyUrl = property.url
      this.propertyMenuOpen = false
      this.propertySearch = ''
    },
    complete () {
      if (!this.ready || this.submitting) return
      this.submitting = true
      const body = {
        propertyUrl: this.propertyUrl,
        locationCode: this.locationCode,
        languageCode: this.languageCode,
        countryEnabled: this.countryEnabled ? 1 : 0,
        datePeriod: this.datePeriod,
        monitoringEnabled: this.monitoringEnabled ? 1 : 0,
        termsAccepted: 1,
      }
      if (this.googleAccount && this.googleAccount.id) {
        body.googleAccountId = this.googleAccount.id
      }
      this.$store.dispatch('connectGsc', { siteId: this.site.id, body })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.syncAuditFlowConfig()
            this.$emit('completed', response.data)
          } else {
            const msg = (response.data && response.data.msg) || 'Could not connect Search Console'
            this.wgToast(msg, null, 'error')
          }
        })
        .catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.msg) ||
            'Could not connect Search Console'
          this.wgToast(msg, null, 'error')
        })
        .finally(() => {
          this.submitting = false
        })
    },
    /**
     * Keep the audit-onboarding GSC step (GoogleDataStep.vue) in sync so a
     * connection made here shows up pre-filled there too.
     */
    syncAuditFlowConfig () {
      const set = (key, value) => this.$store.commit('setInsightKeywordsConfig', { key, value })
      set('wizardLocation', this.locationCode)
      set('wizardLanguage', this.languageCode)
      set('wizardDomain', this.propertyUrl)
      set('wizardIsCountryEnabled', this.countryEnabled)
      const period = this.availablePeriods.find((p) => p.value === this.datePeriod)
      if (period) set('wizardPeriod', period)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './settings-card.scss';

/* GSC CONNECT WIZARD - GoogleDataStep.vue + GoogleLogin.vue */
.wg-gwiz-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: $pop-teal;
  margin-bottom: 8px;
}

.wg-gwiz-sub {
  font-size: 0.95rem;
  line-height: 1.5;
  color: $pop-teal;
  margin: 0 0 24px;
}

.wg-glogin {
  border: 1px solid #e0e4ea;
  background: #fff;
  border-radius: 10px;
  padding: 18px 20px 16px;
  margin-bottom: 24px;
}

.wg-glogin-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.wg-glogin-brand {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  flex: 1;
}

.wg-glogin-gmark {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #2d66ff;
  color: #fff;
  font-weight: 700;
  font-size: 1.15rem;
  line-height: 40px;
  text-align: center;
}

.wg-glogin-user {
  min-width: 0;

  .nm {
    font-weight: 700;
    font-size: 0.95rem;
    color: #003d4d;
    line-height: 1.3;
    word-break: break-word;

    &.ph {
      color: #4a6570;
    }
  }

  .em {
    margin-top: 2px;
    font-size: 0.85rem;
    font-weight: 400;
    color: #5a7a85;
    line-height: 1.35;
    word-break: break-all;

    &.ph {
      color: #6b8794;
    }
  }
}

.wg-glogin-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.wg-connected-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #34a853;
  background: #e6f7ed;
  border: 1px solid #34a853;
  white-space: nowrap;
}

.wg-glogin-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding: 10px 12px;
  background: #f5f6fa;
  border-radius: 8px;
  color: #5c6470;
  font-size: 0.8125rem;
  line-height: 1.45;

  .bico {
    flex-shrink: 0;
    font-size: 1.125rem;
    color: #7a8494;
  }
}

.wg-help-gsc {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: $pop-teal;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  margin-bottom: 20px;

  &:hover {
    color: $pop-accent;
  }

  .material-icons-outlined {
    font-size: 18px;
  }
}

.wg-gfields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 24px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
}

.wg-gfield {
  margin-bottom: 20px;
}

.card-small-title {
  font-size: 1rem;
  font-weight: 800;
  color: $pop-teal;
  margin-bottom: 4px;

  .req {
    color: $pop-danger;
  }
}

.wg-country-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;

  .lbl {
    font-weight: 700;
    color: $pop-teal;
    font-size: 0.85rem;
  }
}

.wg-gwiz-foot {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 8px;

  .sp {
    flex: 1;
  }
}

.wg-gwiz-need {
  font-size: 0.8rem;
  color: $pop-ink-muted;
  white-space: nowrap;
}

/* GSC property picker - DomainSelector.vue */
.wg-dompick {
  position: relative;
}

.wg-dompick-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  background: #fff;
  border: 1px solid $pop-border;
  border-radius: 6px;
  padding: 11px 13px;
  font-size: 0.9rem;
  color: $pop-teal;
  cursor: pointer;
  text-align: left;
  min-height: 44px;

  &:hover {
    border-color: $pop-ink-muted;
  }

  &.open {
    border-color: $pop-link;
    box-shadow: 0 0 0 3px rgba(0, 117, 255, 0.12);
  }

  &.is-disabled {
    cursor: default;
    background: $pop-box-bg;

    &:hover {
      border-color: $pop-border;
    }
  }

  .val {
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ph {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: $pop-ink-faint;
  }

  .chev {
    color: $pop-ink-muted;
    flex: none;
  }
}

.wg-dompick-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 45;
  background: #fff;
  border: 1px solid $pop-border;
  border-radius: 10px;
  box-shadow: $shadow-menu;
  overflow: hidden;

  .dsearch {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid $wg-hairline;

    .material-icons {
      font-size: 1.1rem;
      color: $pop-ink-faint;
    }

    input {
      flex: 1;
      border: none;
      outline: none;
      font-size: 0.85rem;
      color: $pop-teal;
      background: transparent;
    }
  }

  .dlist {
    max-height: 230px;
    overflow-y: auto;
    padding: 6px;
  }

  .dempty {
    padding: 16px 10px;
    text-align: center;
    font-size: 0.85rem;
    color: $pop-ink-muted;
  }

  .ditem {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    border-radius: 6px;
    padding: 9px 10px;
    cursor: pointer;

    &:hover {
      background: $wg-lavender;
    }

    &.sel {
      background: $pop-accent-bg;
    }

    .du {
      display: flex;
      flex-direction: column;
      gap: 2px;
      flex: 1;
      min-width: 0;
    }

    .u {
      font-size: 0.85rem;
      font-weight: 700;
      color: $pop-teal;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .m {
      font-size: 0.75rem;
      color: $pop-ink-muted;
    }

    &.invalid {
      cursor: not-allowed;

      .u {
        color: $pop-ink-faint;
        font-weight: 600;
      }

      .m {
        color: $pop-danger;
      }

      &:hover {
        background: $pop-danger-bg;
      }
    }

    .dc {
      color: $pop-accent;
      font-size: 1.1rem;
      flex: none;
    }
  }

  .dfoot {
    padding: 10px 12px;
    border-top: 1px solid $wg-hairline;
    background: $wg-lavender;
    font-size: 0.75rem;
    color: $pop-ink-muted;
  }
}

.wg-dompick-sel {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 0.8rem;
  color: $pop-success;

  .material-icons {
    font-size: 1rem;
  }

  b {
    color: $pop-teal;
  }

  .clr {
    margin-left: auto;
    background: none;
    border: none;
    padding: 0;
    color: $pop-accent;
    font-size: 0.8rem;
    font-weight: 700;
    cursor: pointer;
    text-decoration: underline;
  }
}
</style>
